import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  limit,
} from "../../../config/firebase";
import { EVENT_STATUS, ERROR_MESSAGES } from "../types/eventTypes";
import {
  generateEventId,
  validateEventData,
  calculateBalance,
  getPaymentStatus,
} from "../utils/eventHelpers";

class EventService {
  getEventCollection() {
    return collection(db, "events");
  }

  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(
        value.seconds * 1000 + (value.nanoseconds || 0) / 1000000,
      ).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  convertDocData(docData) {
    if (!docData) return docData;
    const converted = { ...docData };
    ["createdAt", "updatedAt"].forEach((field) => {
      if (converted[field]) {
        converted[field] = this.convertTimestamp(converted[field]);
      }
    });
    if (converted.eventDetails?.bookingDate) {
      converted.eventDetails = {
        ...converted.eventDetails,
        bookingDate: this.convertTimestamp(converted.eventDetails.bookingDate),
      };
    }
    if (converted.expenses) {
      converted.expenses = converted.expenses.map((e) => ({
        ...e,
        createdAt: this.convertTimestamp(e.createdAt),
      }));
    }
    if (converted.notes) {
      converted.notes = converted.notes.map((n) => ({
        ...n,
        createdAt: this.convertTimestamp(n.createdAt),
      }));
    }
    return converted;
  }

  // ============ EVENT CRUD ============

  async getEvents(filters = {}) {
    try {
      let q = query(this.getEventCollection(), orderBy("createdAt", "desc"));

      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      if (filters.eventType && filters.eventType !== "all") {
        q = query(q, where("eventDetails.eventType", "==", filters.eventType));
      }

      const snapshot = await getDocs(q);
      let events = [];
      snapshot.forEach((docSnap) => {
        // IMPORTANT: spread data FIRST, then override id LAST
        // so the real Firestore doc ID always wins over any
        // custom "id"-like field stored inside the document
        events.push({
          ...this.convertDocData(docSnap.data()),
          id: docSnap.id,
        });
      });

      if (filters.search) {
        const s = filters.search.toLowerCase();
        events = events.filter(
          (e) =>
            e.eventDetails?.eventName?.toLowerCase().includes(s) ||
            e.eventDetails?.customer?.customerName?.toLowerCase().includes(s) ||
            e.eventCode?.toLowerCase().includes(s),
        );
      }

      return events;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getEventById(id) {
    try {
      const docRef = doc(db, "events", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return {
        ...this.convertDocData(docSnap.data()),
        id: docSnap.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createEvent(data, createdBy) {
    try {
      const errors = validateEventData(data);
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors)[0]);
      }

      const eventData = {
        eventCode: generateEventId(), // renamed from "id" to avoid collision with Firestore doc ID
        studioId: data.studioId || "STD001",
        status: data.status || EVENT_STATUS.ENQUIRY,
        priority: data.priority || "medium",
        createdBy: createdBy || "admin",
        isDeleted: false,
        eventDetails: data.eventDetails,
        quotation: data.quotation || {
          quotationId: "",
          quotationNumber: "",
          amount: 0,
          status: "draft",
        },
        invoice: data.invoice || {
          invoiceId: "",
          invoiceNumber: "",
          total: 0,
          advance: 0,
          balance: 0,
          paymentStatus: "pending",
        },
        staff: data.staff || [],
        requiredEquipments: data.requiredEquipments || [],
        schedules: data.schedules || [],
        expenses: data.expenses || [],
        delivery: data.delivery || {
          albumDelivered: false,
          frameDelivered: false,
          pendriveDelivered: false,
          cloudDelivered: false,
          deliveredDate: null,
          deliveredBy: "",
          remarks: "",
        },
        workflow: data.workflow || {
          photoUploaded: false,
          cullingCompleted: false,
          selectionCompleted: false,
          editingCompleted: false,
          albumDesigned: false,
          albumPrinted: false,
          videoEdited: false,
          highlightsCompleted: false,
          reelsCompleted: false,
          delivered: false,
        },
        notes: data.notes || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(this.getEventCollection(), eventData);
      const newDoc = await getDoc(docRef);
      return {
        ...this.convertDocData(newDoc.data()),
        id: newDoc.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateEvent(id, data) {
    try {
      const docRef = doc(db, "events", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      // Never allow "id" or "eventCode" to be overwritten via updateDoc payload
      const safeData = { ...data };
      delete safeData.id;
      delete safeData.eventCode;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return {
        ...this.convertDocData(updatedDoc.data()),
        id: updatedDoc.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateEventStatus(id, status) {
    return this.updateEvent(id, { status });
  }

  async deleteEvent(id) {
    try {
      const docRef = doc(db, "events", id);
      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async permanentlyDeleteEvent(id) {
    try {
      await deleteDoc(doc(db, "events", id));
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ STAFF MANAGEMENT ============

  async assignStaff(eventId, staffMember) {
    try {
      const event = await this.getEventById(eventId);
      const staff = [...(event.staff || []), staffMember];
      return await this.updateEvent(eventId, { staff });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateStaffMember(eventId, userId, updates) {
    try {
      const event = await this.getEventById(eventId);
      const staff = (event.staff || []).map((s) =>
        s.userId === userId ? { ...s, ...updates } : s,
      );
      return await this.updateEvent(eventId, { staff });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeStaff(eventId, userId) {
    try {
      const event = await this.getEventById(eventId);
      const staff = (event.staff || []).filter((s) => s.userId !== userId);
      return await this.updateEvent(eventId, { staff });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async markAttendance(eventId, userId, attendance) {
    return this.updateStaffMember(eventId, userId, { attendance });
  }

  // ============ EQUIPMENT MANAGEMENT ============

  async assignEquipment(eventId, equipmentItem) {
    try {
      const event = await this.getEventById(eventId);
      const requiredEquipments = [
        ...(event.requiredEquipments || []),
        equipmentItem,
      ];
      return await this.updateEvent(eventId, { requiredEquipments });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateEquipmentItem(eventId, equipmentId, updates) {
    try {
      const event = await this.getEventById(eventId);
      const requiredEquipments = (event.requiredEquipments || []).map((eq) =>
        eq.equipmentId === equipmentId ? { ...eq, ...updates } : eq,
      );
      return await this.updateEvent(eventId, { requiredEquipments });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async markEquipmentTaken(eventId, equipmentId, takenBy) {
    return this.updateEquipmentItem(eventId, equipmentId, {
      officeTaken: true,
      takenBy,
      takenAt: new Date().toISOString(),
    });
  }

  async markEquipmentReturned(eventId, equipmentId, returnedBy) {
    return this.updateEquipmentItem(eventId, equipmentId, {
      returned: true,
      returnedBy,
      returnedAt: new Date().toISOString(),
    });
  }

  async removeEquipment(eventId, equipmentId) {
    try {
      const event = await this.getEventById(eventId);
      const requiredEquipments = (event.requiredEquipments || []).filter(
        (eq) => eq.equipmentId !== equipmentId,
      );
      return await this.updateEvent(eventId, { requiredEquipments });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ SCHEDULE MANAGEMENT ============

  async addSchedule(eventId, scheduleDay) {
    try {
      const event = await this.getEventById(eventId);
      const schedules = [...(event.schedules || []), scheduleDay];
      return await this.updateEvent(eventId, { schedules });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateSchedule(eventId, dayId, updates) {
    try {
      const event = await this.getEventById(eventId);
      const schedules = (event.schedules || []).map((s) =>
        s.id === dayId ? { ...s, ...updates } : s,
      );
      return await this.updateEvent(eventId, { schedules });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeSchedule(eventId, dayId) {
    try {
      const event = await this.getEventById(eventId);
      const schedules = (event.schedules || []).filter((s) => s.id !== dayId);
      return await this.updateEvent(eventId, { schedules });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ EXPENSES ============

  async addExpense(eventId, expense) {
    try {
      const event = await this.getEventById(eventId);
      const newExpense = {
        ...expense,
        expenseId: `EXP${Date.now()}`,
        createdAt: serverTimestamp(),
      };
      const expenses = [...(event.expenses || []), newExpense];
      return await this.updateEvent(eventId, { expenses });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeExpense(eventId, expenseId) {
    try {
      const event = await this.getEventById(eventId);
      const expenses = (event.expenses || []).filter(
        (e) => e.expenseId !== expenseId,
      );
      return await this.updateEvent(eventId, { expenses });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ DELIVERY & WORKFLOW ============

  async updateDelivery(eventId, deliveryUpdates) {
    try {
      const event = await this.getEventById(eventId);
      const delivery = { ...event.delivery, ...deliveryUpdates };
      return await this.updateEvent(eventId, { delivery });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateWorkflow(eventId, workflowUpdates) {
    try {
      const event = await this.getEventById(eventId);
      const workflow = { ...event.workflow, ...workflowUpdates };
      return await this.updateEvent(eventId, { workflow });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ QUOTATION / INVOICE ============

  async updateQuotation(eventId, quotationUpdates) {
    try {
      const event = await this.getEventById(eventId);
      const quotation = { ...event.quotation, ...quotationUpdates };
      return await this.updateEvent(eventId, { quotation });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateInvoice(eventId, invoiceUpdates) {
    try {
      const event = await this.getEventById(eventId);
      const merged = { ...event.invoice, ...invoiceUpdates };
      merged.balance = calculateBalance(merged.total, merged.advance);
      merged.paymentStatus = getPaymentStatus(merged.total, merged.advance);
      return await this.updateEvent(eventId, { invoice: merged });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ NOTES ============

  async addNote(eventId, note, createdBy) {
    try {
      const event = await this.getEventById(eventId);
      const newNote = { note, createdBy, createdAt: serverTimestamp() };
      const notes = [...(event.notes || []), newNote];
      return await this.updateEvent(eventId, { notes });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ STATISTICS ============

  async getEventStats() {
    try {
      const events = await this.getEvents();
      const active = events.filter((e) => e.isDeleted !== true);

      const byStatus = {};
      Object.values(EVENT_STATUS).forEach((s) => {
        byStatus[s] = active.filter((e) => e.status === s).length;
      });

      const totalRevenue = active.reduce(
        (sum, e) => sum + (e.invoice?.total || 0),
        0,
      );
      const totalCollected = active.reduce(
        (sum, e) => sum + (e.invoice?.advance || 0),
        0,
      );
      const totalExpenses = active.reduce(
        (sum, e) =>
          sum + (e.expenses || []).reduce((s, ex) => s + (ex.amount || 0), 0),
        0,
      );

      return {
        totalEvents: active.length,
        byStatus,
        totalRevenue,
        totalCollected,
        totalPending: totalRevenue - totalCollected,
        totalExpenses,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ EXPENSES ============

  async addExpense(eventId, expense) {
    try {
      const event = await this.getEventById(eventId);
      const newExpense = {
        ...expense,
        expenseId: `EXP${Date.now()}`,
        createdAt: new Date().toISOString(), // was: serverTimestamp()
      };
      const expenses = [...(event.expenses || []), newExpense];
      return await this.updateEvent(eventId, { expenses });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ NOTES ============

  async addNote(eventId, note, createdBy) {
    try {
      const event = await this.getEventById(eventId);
      const newNote = {
        note,
        createdBy,
        createdAt: new Date().toISOString(), // was: serverTimestamp()
      };
      const notes = [...(event.notes || []), newNote];
      return await this.updateEvent(eventId, { notes });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ AVAILABILITY CHECK ============

  async checkStaffAvailability(userId, date, excludeEventId = null) {
    try {
      const events = await this.getEvents();
      const conflict = events.some((event) => {
        if (event.id === excludeEventId) return false;
        return event.schedules?.some(
          (s) => s.date === date && s.staffIds?.includes(userId),
        );
      });
      return !conflict;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async checkEquipmentAvailability(equipmentId, date, excludeEventId = null) {
    try {
      const events = await this.getEvents();
      const conflict = events.some((event) => {
        if (event.id === excludeEventId) return false;
        const hasDate = event.schedules?.some((s) => s.date === date);
        if (!hasDate) return false;
        return event.requiredEquipments?.some(
          (eq) =>
            eq.equipmentId === equipmentId && eq.officeTaken && !eq.returned,
        );
      });
      return !conflict;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    console.error("Firebase error:", error);
    if (error.code === "permission-denied") {
      return new Error("You don't have permission to perform this action.");
    }
    return new Error(error.message || "An error occurred");
  }
}

const eventService = new EventService();
export default eventService;
